import { DataGrid } from "@mui/x-data-grid";
import { Box, Stack, Tooltip, Typography } from "@mui/material";
import { useResponsive } from "../../../core/hooks/useResponsive";
import SaleHoldingCard from "./SaleHoldingCard";

const plTone = (v) => (v > 0 ? "success.main" : v < 0 ? "error.main" : "text.secondary");
const EST_HINT = {
  target: "At the stop profit / trigger (price is at or above the buy price)",
  stop: "At the stop loss (price is below the buy price)",
  trailing: "At the next trigger / at the locked exit",
};

/** Est. P/L: one value, or "next trigger / locked exit" once a Reactive holding has triggered. */
export const EstPL = ({ row }) => {
  if (row.estPL == null) return "—";
  return (
    <Tooltip title={EST_HINT[row.estBasis] || ""}>
      <Box component="span" sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>
        <Box component="span" sx={{ color: plTone(row.estPL) }}>{row.estPL}</Box>
        {row.estPLAlt != null && (
          <>
            <Box component="span" sx={{ color: "text.disabled", mx: 0.5 }}>/</Box>
            <Box component="span" sx={{ color: plTone(row.estPLAlt) }}>{row.estPLAlt}</Box>
          </>
        )}
      </Box>
    </Tooltip>
  );
};

const getColorClass = (val1, val2 = 0) => {
  if (val1 > val2) return "profit-cell";
  if (val1 < val2) return "loss-cell";
  return "neutral-cell";
};

// minWidths added so the ten columns stop collapsing into each other at tablet width;
// flex ratios are unchanged, so a wide desktop renders exactly as before.
const saleHoldingsColumns = [
  { field: "symbol", headerName: "Symbol", flex: 1, minWidth: 110 },
  { field: "qty", headerName: "Quantity", flex: 1, minWidth: 80 },
  { field: "buyPrice", headerName: "Buy Price", flex: 1, minWidth: 95 },
  { field: "marketPrice", headerName: "Market Price", flex: 1, minWidth: 105 },
  { field: "stopProfit", headerName: "Target / Next trigger", flex: 1.2, minWidth: 130 },
  { field: "stopLoss", headerName: "Stop / Locked exit", flex: 1.2, minWidth: 125 },
  {
    field: "curPL",
    headerName: "Cur. P/L",
    flex: 1,
    minWidth: 90,
    valueFormatter: (value, row) => (row?.isSold ? "—" : value),
    cellClassName: (params) => (params.row.isSold ? "neutral-cell" : getColorClass(params.value)),
  },
  {
    // At the stop profit / trigger (price >= buy) or the stop loss (price < buy); once a Reactive
    // holding has triggered: at the next trigger / at the locked exit.
    field: "estPL",
    headerName: "Est. P/L",
    flex: 1.3,
    minWidth: 120,
    sortable: false,
    renderCell: (params) => <EstPL row={params.row} />,
  },
  {
    field: "isSold",
    headerName: "State",
    flex: 1.2,
    minWidth: 110,
    // Order pending = a live sell order placed but not yet filled (re-checked every scan);
    // Trigger watch = Reactive, past its trigger - its exit is locked in and moves up with each new trigger.
    renderCell: (params) =>
      params.value ? "Sold" : params.row.orderState === "pending" ? "Order pending" : params.row.triggered ? "Trigger watch" : "Watching",
  },
  { field: "salePrice", headerName: "Sale Price", flex: 1, minWidth: 95 },
  {
    field: "actualPL",
    headerName: "Actual P/L",
    flex: 1,
    minWidth: 95,
    cellClassName: (params) => getColorClass(params.value),
  },
];

// tradeId, not symbol: the same stock bought twice is two rows (duplicate ids break the grid).
const rowKey = (row) => row.tradeId ?? `${row.symbol}_${row.buyPrice ?? ""}_${row.qty ?? ""}`;

const SaleHoldingsGrid = ({ holdings }) => {
  const { isCompact } = useResponsive();
  const rows = holdings || [];

  if (isCompact) {
    // Ten flex columns leave roughly 34px each on a phone, which is unreadable, so
    // every field moves onto a card instead.
    if (rows.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 3 }}>
          No holdings yet.
        </Typography>
      );
    }
    return (
      <Stack spacing={1} sx={{ pr: 0.25 }}>
        {rows.map((row) => (
          <SaleHoldingCard key={rowKey(row)} row={row} />
        ))}
      </Stack>
    );
  }

  return (
    <Box sx={{ height: "100%", width: "100%" }}>
      <DataGrid
        rows={rows}
        columns={saleHoldingsColumns}
        getRowId={rowKey}
        rowHeight={35}
        hideFooter
        sx={{ height: "100%" }}
      />
    </Box>
  );
};

export default SaleHoldingsGrid;
